const getTrackPaddingStart = (track) => {
  const style = getComputedStyle(track);
  return parseFloat(style.paddingInlineStart) || parseFloat(style.paddingLeft) || 0;
};

const getDefaultPositions = (track, slideSelector) => {
  const paddingStart = getTrackPaddingStart(track);
  const trackRect = track.getBoundingClientRect();

  return Array.from(track.querySelectorAll(slideSelector))
    .map((slide) => {
      const slideRect = slide.getBoundingClientRect();
      return Math.max(0, slideRect.left - trackRect.left + track.scrollLeft - paddingStart);
    })
    .sort((left, right) => left - right)
    .filter((position, index, positions) => {
      if (index === 0) return true;
      return Math.abs(position - positions[index - 1]) > 1;
    });
};

const getScrollState = (track) => {
  const { scrollLeft, scrollWidth, clientWidth } = track;
  const hasOverflow = scrollWidth > clientWidth + 1;

  if (!hasOverflow) {
    return {
      hasOverflow,
      atStart: true,
      atEnd: true,
    };
  }

  return {
    hasOverflow,
    atStart: scrollLeft <= 1,
    atEnd: scrollLeft + clientWidth >= scrollWidth - 1,
  };
};

const getScrollBehavior = () => {
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    return 'auto';
  }

  return 'smooth';
};

export const initAccCarousel = ({
  root,
  track,
  prevBtn,
  nextBtn,
  slideSelector,
  getPositions,
  onStateChange,
}) => {
  if (!track || !prevBtn || !nextBtn) return () => {};

  const abortController = new AbortController();
  const { signal } = abortController;
  let scrollTimeout = null;
  let resizeObserver = null;
  let frameId = null;

  const scheduleNotifyState = () => {
    if (frameId !== null) return;

    frameId = window.requestAnimationFrame(() => {
      frameId = null;
      notifyState();
    });
  };

  const resolvePositions = () => {
    if (typeof getPositions === 'function') {
      return getPositions(track);
    }

    return getDefaultPositions(track, slideSelector);
  };

  const notifyState = () => {
    const state = getScrollState(track);

    if (typeof onStateChange === 'function') {
      onStateChange(state, { root, track, prevBtn, nextBtn });
    } else {
      prevBtn.hidden = !state.hasOverflow || state.atStart;
      nextBtn.hidden = !state.hasOverflow || state.atEnd;
    }
  };

  const scrollToAdjacentSlide = (direction) => {
    const positions = resolvePositions();
    if (!positions.length) return;

    const current = track.scrollLeft;
    const tolerance = 2;
    let target = positions[0];

    if (direction === 'next') {
      target = positions.find((position) => position > current + tolerance);
      if (target === undefined) {
        target = positions[positions.length - 1];
      }
    } else {
      const reversed = [...positions].reverse();
      target = reversed.find((position) => position < current - tolerance);
      if (target === undefined) {
        target = positions[0];
      }
    }

    track.scrollTo({ left: target, behavior: getScrollBehavior() });
  };

  prevBtn.addEventListener('click', () => {
    scrollToAdjacentSlide('prev');
  }, { signal });

  nextBtn.addEventListener('click', () => {
    scrollToAdjacentSlide('next');
  }, { signal });

  track.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(notifyState, 100);
  }, { passive: true, signal });

  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver(() => {
      scheduleNotifyState();
    });
    resizeObserver.observe(track);
    if (root && root !== track) {
      resizeObserver.observe(root);
    }
  } else {
    window.addEventListener('resize', scheduleNotifyState, { passive: true, signal });
  }

  notifyState();

  return () => {
    abortController.abort();
    resizeObserver?.disconnect();
    clearTimeout(scrollTimeout);
    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
    }
  };
};
